import { type RefObject, useLayoutEffect } from 'react';

/**
 * A TEXTAREA THAT GROWS WITH WHAT IS IN IT, never below the height its `rows` asks for.
 *
 * ONE IMPLEMENTATION, TWO CALLERS. The multiline TEXT input (`inbound-intake.md` D18) grew
 * this way first and the inbound mapping editor (D19) needs exactly the same behaviour with
 * a different floor. Two copies of a measurement is how the two drift apart - `measure.ts`
 * says the same thing about the one rule it merged - so the recipe lives here, beside the
 * primitive it measures, and the floor stays the caller's through `rows`.
 *
 * THE ORDER IS THE WHOLE TRICK. `height` is cleared FIRST so the box falls back to the
 * height `rows` asks for, which is both the floor and the reference for the borders
 * (`INPUT_BASE` is `box-sizing: border-box`, so a height set from `scrollHeight` - content
 * plus padding - would be short by exactly the border and show a scrollbar). Then
 * `scrollHeight`, which is never smaller than the cleared box, becomes the new height. It
 * runs on every value change, so deleting a paragraph shrinks the box back.
 *
 * The zero guard is not defensive noise: a unit environment has no layout engine, every
 * measurement is 0, and setting `height: 0px` would collapse the control in exactly the
 * tests that check it renders. With no layout there is nothing to grow to, and `rows` is
 * already the whole answer.
 */
export function useAutoGrow(ref: RefObject<HTMLTextAreaElement | null>, text: string): void {
  /*
   * `text` IS the dependency, and the rule cannot see it: the effect measures the box React
   * has just filled with the value, so the value reaches it through the DOM rather than
   * through the closure. Dropping it would fit the box once and never again.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: the dependency is the rendered value
  useLayoutEffect(() => {
    const node = ref.current;
    if (node === null) return;
    node.style.height = '';
    if (node.clientHeight === 0) return;
    const borders = node.offsetHeight - node.clientHeight;
    node.style.height = `${node.scrollHeight + borders}px`;
  }, [text]);
}
